import { ChangeDetectorRef, Component, isDevMode, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { UserData } from '../../data-models/user-models/user-data.model';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-resources',
    templateUrl: './resources.component.html',
    styleUrls: ['./resources.component.css'],
    standalone: false
})
export class ResourcesComponent implements OnInit {

  isDataLoaded: boolean = false; // flag that tells us if the HTML is ready to be rendered
  showMenu = false; 

  // define species guide pdfs
  speciesGuides: string[][] = [
    ['Pacific Coastal Birds', 'jPacific_Coastal_Birds_2012.pdf'],
    ['Sturgeon ID Sheet', 'lSturgeon ID Sheet_RG.pdf'],
    ['Common Groundfish', 'icommongroundfish_poster.pdf'],
    ['Marine Mammal Guide', 'kBCCSN ID Guide_2010 revised.pdf']
  ];

  userDetails: UserData = new UserData();
  gillNetMaps: string[][] = [['License Areas (C-E)', 'ggearareamapCDEforGillnet.pdf'], ['Subareas For Nets', 'hLogbook Subareas For Nets.pdf']];
  seineMaps: string[][] = [['License Areas (A-B)', 'ggearareamapsprintABforSeine.pdf'], ['Subareas For Nets', 'hLogbook Subareas For Nets.pdf']];
  trollMaps: string[][] = [['License Areas (F-H)', 'lgearareamapsF-H.pdf'], ['Zones and Subareas For Troll', 'mLogbook Zones and Subareas For Troll - colour.pdf']];
  allAreaMaps: string[][] = [['Area 1', '01-eng.pdf'], ['Area 101', '101-eng.pdf'], ['Area 2 Graham', '02-graham-eng.pdf'], ['Area 2 Moresby', '02-moresby-eng.pdf'],
                            ['Area 102', '102-eng.pdf'],['Area 142', '142-eng.pdf'],['Area 3', '03-eng.pdf'],['Area 103', '103-eng.pdf'],['Area 4', '04-eng.pdf'],
                            ['Area 104', '104-eng.pdf'],['Area 5', '05-eng.pdf'],['Area 105', '105-eng.pdf'],['Area 6', '06-eng.pdf'],['Area 106', '106-eng.pdf'],
                            ['Area 7', '07-eng.pdf'],['Area 107', '107-eng.pdf'],['Area 8', '08-eng.pdf'],['Area 108', '108-eng.pdf'], ['Area 9', '09-eng.pdf'],
                            ['Area 109', '109-eng.pdf'],['Area 130', '130-eng.pdf'], ['Area 10', '10-eng.pdf'],['Area 110', '110-eng.pdf'],['Area 11', '11-eng.pdf'],
                            ['Area 111', '111-eng.pdf'],['Area 12', '12-eng.pdf'],['Area 13', '13-eng.pdf'],['Area 14', '14-eng.pdf'],['Area 15', '15-eng.pdf'],
                            ['Area 16', '16-eng.pdf'],['Area 17', '17-eng.pdf'],['Area 18', '18-eng.pdf'],['Area 19', '19-eng.pdf'],['Area 20', '20-eng.pdf'],
                            ['Area 21 & 22', '21-22-eng.pdf'],['Area 121', '121-eng.pdf'],['Area 23', '23-eng.pdf'],['Area 123', '123-eng.pdf'],['Area 24', '24-eng.pdf'],
                            ['Area 124', '124-eng.pdf'],['Area 25', '25-eng.pdf'],['Area 125', '125-eng.pdf'],['Area 26', '26-eng.pdf'],['Area 126', '126-eng.pdf'],
                            ['Area 27', '27-eng.pdf'],['Area 127', '127-eng.pdf'],['Area 28', '28-eng.pdf'],['Area 29', '29-eng.pdf']];
  showPdf: boolean = false;
  path: string = environment.resourcePath;
  filename: string = '';
  logbooks: string[] = [];
  isDownloading: boolean = false;
  downloadProgress: number = 0;

  constructor(private ref: ChangeDetectorRef, private http: HttpClient) {}


  ngOnInit(): void {
    this.userDetails = UserStorageService.currUser;
    this.showMenu = !!this.userDetails?.userEmail?.trim();
    this.initialize();
  }

  
  /*  ---------- Page Initialization Methods  ---------- */


  initialize(): void {

    this.addMaps();
    this.isDataLoaded = true; // set flag to render HTML
    this.ref.detectChanges(); // re-render HTML

  }

  // add all maps to the logbooks array (used for HTML display)
  addMaps(): void {

    this.logbooks.push('Gill Net');
    this.logbooks.push('Seine');
    this.logbooks.push('Troll');
    this.logbooks.push('All Area');

  }


  /*  ---------- Resource Methods  ---------- */


  // which resource option the user clicked
  resourceClick(type: number, index: number): void {

    // species identification guide
    if (type === 0) {
      this.filename = this.speciesGuides[index][1];
    }

    // maps
    else if (type === 1) {
      this.filename = this.gillNetMaps[index][1];
    }
    else if (type === 2) {
      this.filename = this.seineMaps[index][1];
    }
    else if (type === 3) {
      this.filename = this.trollMaps[index][1];
    } 
    else if (type === 4) {
      this.filename = this.allAreaMaps[index][1];
    }
    
    else { // if error
      console.log('invalid resource type');
      return;
    }

    this.showPdf = true;

  }

  // back to resources list
  backToResources(): void {

    this.showPdf = false;
    this.filename = '';

  }

  // download all PDF resources as a single ZIP file
  async downloadAll(): Promise<void> {

    this.isDownloading = true;
    this.downloadProgress = 0;

    // collect unique filenames across all resource categories
    const allEntries = [
      ...this.speciesGuides,
      ...this.gillNetMaps,
      ...this.seineMaps,
      ...this.trollMaps,
      ...this.allAreaMaps
    ];
    const uniqueFilenames = [...new Set(allEntries.map(entry => entry[1]))];
    const total = uniqueFilenames.length;

    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();

    for (let i = 0; i < uniqueFilenames.length; i++) {
      const filename = uniqueFilenames[i];
      try {
        const blob = await firstValueFrom(this.http.get(this.path + filename, { responseType: 'blob' }));
        zip.file(filename, blob);
      } catch {
        console.warn(`Could not fetch resource: ${filename}`);
      }
      this.downloadProgress = Math.round(((i + 1) / total) * 100);
      this.ref.detectChanges();
    }

    const content = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(content);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'elog-resources.zip';
    a.click();
    URL.revokeObjectURL(url);

    this.isDownloading = false;
    this.ref.detectChanges();

  }

}
